import { defineNuxtPlugin, useRuntimeConfig } from '#app'
import { feathers } from '@feathersjs/feathers'
import rest from '@feathersjs/rest-client'
import authentication from '@feathersjs/authentication-client'
import type { Application, Query } from '@feathersjs/feathers'
import type { AuthenticationResult } from '@feathersjs/authentication'
import { createClient as createLocationsClient, type Location, type MachineProfile } from '@safetynet/roci-api-locations'
import { createClient as createReportsClient } from '@safetynet/roci-api-reports'
import { createClient as createIntegrationsClient, type MocaEventFetch, type MocaEventsResult } from '@safetynet/roci-api-integrations'
import { createClient as createCompaniesClient, type Company, type DatabaseSettings } from '@safetynet/roci-api-companies'
import { createClient as createUsersClient, type Nationality, type NoteTypes, type PhoneType, type Title, type User, type UserAnnounceProtocol, type UserFields, type UserPhoto, type UserSpecialAssistanceNeed, type ReserveDetails, type Issuable, type IssuableItemCategory } from '@safetynet/roci-api-users'
import { createClient as createBookingsClient, type BookingData, type BookingResult, type Validate, type ValidateData, type ValidateResult, type Visit, type VisitUserType, type VisitType, type VisitUserField, type CheckinData, type CheckoutResult, type CheckinResult, type CreateBookingResult, type Visitor, type VisitorResult, type VisitUser, type VisitUserDetail, type VisitUserRequiredEntryField } from '@safetynet/roci-api-bookings'
import { createClient as createCustomFieldTypesClient, type CustomFieldType } from '@safetynet/roci-api-custom-field-types'
import MiniSearch from 'minisearch'
import { createClient as CreateClientConfigClient, type ClientConfigResult } from '@safetynet/roci-api-integrations'

interface BrowserDbSetting {
  capitalise: boolean
}
type GUID = `${string}-${string}-${string}-${string}-${string}`

export interface UpdateVisitScheduleResult {
  visitId: string
  fromDate: string
  toDate: string
  edited: string
}

export interface DataService {
  authenticate: (strategy: string, data: any) => Promise<any>
  bookingCreate: (booking: BookingData) => Promise<CreateBookingResult>
  bookingValidate: (booking: ValidateData) => Promise<ValidateResult[]>
  createCompanyByTenant: (company: Company) => Promise<Company | null>
  createPasswordResetToken: (login: string, database: string) => Promise<number>
  createUserByTenant: (user: User) => Promise<User>
  fetchAndUpdateNewEvents: (deviceSerial: number) => Promise<MocaEventsResult[]>
  findCompaniesByTenant: (query: Query) => Promise<Company[]>
  findCompaniesFuzzyByTenant: (search: string) => Promise<Company[]>
  findCustomFieldTypesByTenant: () => Promise<CustomFieldType[]>
  findDomains: (params?: any) => Promise<any>
  findUserTenants: (login: string) => Promise<any>
  findLocationsByTenant: (query: Query) => Promise<Location[]>
  findNationalities: () => Promise<Nationality[]>
  findSkyVisitor: (params?: any) => Promise<any>
  findDomainTenants: (domain: string) => Promise<any>
  findTenants: (params?: any) => Promise<any>
  findTenantUsers: (params?: any) => Promise<any>
  findUserAnnounceProtocolsByTenant: () => Promise<UserAnnounceProtocol[]>
  findUserByTenant: (userId: string) => Promise<User>
  findUserFieldsByTenant: () => Promise<UserFields[]>
  findUserPhoneTypesByTenant: () => Promise<PhoneType[]>
  findUserPhotoByTenant: (userId: string) => Promise<UserPhoto | null>
  findUsers: (params?: any) => Promise<any>
  checkUserPermissions: (params: { permissionIds: number[] }) => Promise<number[]>
  findUsersByTenant: (query: Query) => Promise<User[]>
  findUsersNoteTypesByTenant: (query: Query) => Promise<NoteTypes[]>
  findUserSpecialAssitanceNeedByTenant: () => Promise<UserSpecialAssistanceNeed[]>
  findUsersPhotoByTenant: (userIds: string[]) => Promise<UserPhoto[]>
  findUsersTitlesByTenant: (query: Query) => Promise<Title[]>
  findUsersUserFieldsByTenant: (query: Query) => Promise<UserFields[]>
  findVisitByTenant: (visitId: string) => Promise<Visit>
  findVisitsByTenant: (query: Query) => Promise<Visit[]>
  findVisitTypesByTenant: (query: Query) => Promise<VisitType[]>
  findVisitUserFieldsByTenant: () => Promise<VisitUserField[]>
  findVisitUserRequiredEntryFieldsByTenant: () => Promise<VisitUserRequiredEntryField[]>
  findVisitUsersTypesByTenant: (query: Query) => Promise<VisitUserType[]>
  findIssuableItemCategories: (query: Query) => Promise<IssuableItemCategory[]>
  getAccessToken: () => Promise<string | null>
  getBrowserDbSettings: () => Promise<BrowserDbSetting>
  getConfiguration: (id: number) => Promise<string>
  getCompanyByTenant: (companyId: string) => Promise<Company | null>
  getCurrentUser: () => Promise<any>
  getMachineProfileByCode: (tenantName: string, machineProfileCode: GUID) => Promise<MachineProfile>
  getTenant: () => Promise<any>
  getTenantId: () => string | undefined
  getVisitUserByTenant: (visitUserId: string) => Promise<VisitUserDetail>
  logout: () => Promise<AuthenticationResult | null>
  patchUserByTenant: (userId: string, user: User) => Promise<User>
  patchVisitUser: (data: { visitUserId: string } & Partial<VisitUserDetail>, selfEdit: boolean) => Promise<boolean>
  reAuthenticate: () => Promise<AuthenticationResult | null>
  reserveCardForVisitor: (cardData: ReserveDetails) => Promise<boolean>
  resetPassword: (login: string, database: string, token: string, password: string) => Promise<number>
  toggleSimpleBooking: () => Promise<boolean>
  updateVisitSchedule: (visitId: string, fromDate: string, toDate: string) => Promise<UpdateVisitScheduleResult>
  updateUserPhotoByTenant: (userId: string, photoBase64: string) => Promise<boolean>
  validatePasswordResetToken: (login: string, database: string, token: string) => Promise<number>
  visitorCheckin: (visitId: string, visitUserId: string, proceed?: boolean, first?: boolean) => Promise<CheckinResult>
  visitorCheckout: (visitId: string, visitUserId: string, proceed?: boolean, first?: boolean) => Promise<CheckoutResult>
  visitorsValidate: (locations: number[], visitors: Visitor[], forCheckin: boolean | string) => Promise<VisitorResult | null>
  executeReport: (reportName: string, payload: any) => Promise<any>
  getPass: (data: { visitId: string, visitUserId: string }[]) => Promise<{ data: string }>
}

const authedFetch = async (input: string, init: RequestInit) => {
  init.headers = {
    ...init.headers,
  }
  if (import.meta.client) {
    // @ts-ignore
    init.headers.Authorization = `Bearer ${localStorage.getItem('feathers-jwt')}`
    return window.fetch(input, init)
  }
  return fetch(input, init)
}

export default defineNuxtPlugin((nuxtApp) => {
  const app: Application = feathers()
  // Use REST, not SocketIO
  const client = rest()
  const transport = rest(useRequestURL().origin + '/api').fetch(authedFetch)
  // Check if running on the client side
  if (import.meta.client) {
    // On the client, use the window.fetch API
    app.configure(client.fetch(window.fetch.bind(window)))
  }

  const locationClient = createLocationsClient(transport as any)
  const companiesClient = createCompaniesClient(transport as any)
  const usersClient = createUsersClient(transport as any)
  const bookingClient = createBookingsClient(transport as any)
  const customFieldTypesClient = createCustomFieldTypesClient(transport as any)
  const integrationClient = createIntegrationsClient(transport as any)
  const reportClient = createReportsClient(transport as any)
  // Configure an AJAX library (see below) with that client
  // app.configure(client.fetch(window.fetch.bind(window)))
  // NOTE: This points to the proxy to protect the API key
  app.configure(authentication({ path: '/api/auth/v1/authentication' }))
  const defaultQuery = { deleted: false, enabled: true }
  const currentRoute = useRoute()
  let cachedBrowserDbSettings: BrowserDbSetting | null

  const getTenantId = () => {
    // // Get the tenant ID from the local storage if it's there
    // if (import.meta.env.SSR) return
    // if (localStorage.getItem('tenant')) {
    //   const tenant = JSON.parse(localStorage.getItem('tenant') as string)
    //   return tenant.database
    // }

    let tenant
    if (import.meta.client) {
      tenant = document.cookie.replaceAll('; ', ';').split(';').find(c => c.startsWith('tenant='))?.split('=')[1]
    }
    else {
      const req = nuxtApp.ssrContext?.event?.req // Check if SSR context exists
      const cookies = req?.headers?.cookie
      tenant = cookies?.replaceAll('; ', ';').split(';').find(c => c.startsWith('tenant='))?.split('=')[1]
    }
    return tenant
  }

  const dataService: DataService = {
    getTenantId: getTenantId,
    authenticate: async (strategy, data) => {
      return await app.authenticate({ strategy, ...data })
    },
    logout: async (): Promise<AuthenticationResult | null> => {
      return await app.logout()
    },
    getMachineProfileByCode: async (tenantName: string, machineProfileCode: GUID): Promise<MachineProfile> => {
      return locationClient.service('/machine-profile/v1/:tenantId').getByCode({ loginCode: machineProfileCode }, { route: { tenantId: tenantName } }) as any as MachineProfile
    },
    getAccessToken: async () => {
      return app.authentication.getAccessToken()
    },
    reAuthenticate: async () => {
      return await app.reAuthenticate()
    },
    getCurrentUser: async () => {
      await app.reAuthenticate()
      return app.get('authentication')
    },
    getTenant: async () => {
      const tenantId = await getTenantId()
      if (!tenantId) throw new Error('getTenant: TenantId is required')
      return await app.service('api/auth/v1/tenants').get(tenantId)
    },
    getBrowserDbSettings: async (): Promise<BrowserDbSetting> => {
      const tenantId = await getTenantId()
      if (!tenantId) throw new Error('getBrowserDbSettings: TenantId is required')
      if (cachedBrowserDbSettings) return cachedBrowserDbSettings
      const db = await companiesClient.service('/company/v1/:tenantId/database-setting').find({ paginate: false, route: { tenantId } }) as any as DatabaseSettings[]
      cachedBrowserDbSettings = {
        capitalise: db.find(db => db.setting == 'Capitalise')?.value == 'True',
      }
      return cachedBrowserDbSettings
    },
    getConfiguration: async (id: number): Promise<string> => {
      const tenantId = await getTenantId()
      if (!tenantId) throw new Error('getConfiguration: TenantId is required')
      return (await companiesClient.service('/company/v1/:tenantId/configuration').get(id, { route: { tenantId } }) as { configurationValue: string }).configurationValue
    },
    findUsers: async (params = {}) => {
      const userService = app.service('api/auth/v1/users')
      const query = { ...defaultQuery, ...params.query }
      return await userService.find({ ...params, query })
    },
    checkUserPermissions: async (params = { permissionIds: [] as number[] }, tenant: string) => {
      const tenantId = tenant ?? await getTenantId()
      if (!tenantId) throw new Error('checkUserPermissions: TenantId is required')
      return (await usersClient.service('/user/v1/:tenantId').hasPermissions(params.permissionIds, { route: { tenantId } })).allowed
    },
    findDomains: async () => {
      const config = await useConfig()
      const response = await fetch(config.apiUrl + '/auth/v1/domains', {
        headers: {
          'x-api-key': (await config).apiKeys[0].apiKey,
        },
      })
      const result = await response.json()
      return result.data
    },
    findDomainTenants: async (domainId: string) => {
      const config = await useConfig()
      const response = await fetch(config.apiUrl + `/auth/v1/domain-tenants?domainId=${domainId}`, {
        headers: {
          'x-api-key': (await config).apiKeys[0].apiKey,
        },
      })

      const result = await response.json()

      if (result.data.length > 0) {
        const tenantResponse = await fetch(config.apiUrl + `/auth/v1/tenants?${result.data.map((d: any) => 'id[$in][]=' + d.tenantId).join('&')}`, {
          headers: {
            'x-api-key': (await config).apiKeys[0].apiKey,
          },
        })
        const tenants = await tenantResponse.json()
        result.data.map((d: any) => {
          d.database = tenants.data.find((t: any) => t.id == d.tenantId)?.database
        })
      }

      return result
      /* if (result.data.length > 0) {
        const tenantResponse = await fetch(config.apiUrl + `/auth/v1/tenants?${result.data.map((d: any) => 'id[$in][]=' + d.tenantId).join('&')}`, {
          headers: {
            'x-api-key': (await config).apiKeys[0].apiKey,
          },
        })
        const tenants = await tenantResponse.json()
        return tenants.data
      }
      return [] */
    },
    findUserTenants: async (login: string) => {
      const config = await useConfig()
      const response = await fetch(config.apiUrl + `/auth/v1/tenant-users`, {
        method: 'POST',
        headers: {
          'x-api-key': (await config).apiKeys[0].apiKey,
          'X-Service-Method': 'forUser',
        },
        body: JSON.stringify({ login }),
      })
      const resultTenant = await response.json()
      return resultTenant.tenants
    },
    findTenants: async (params = {}) => {
      const tenantService = app.service('/api/auth/v1/tenants')
      const query = { ...defaultQuery, ...params.query }
      return await tenantService.find({ ...params, query })
    },
    findTenantUsers: async (params = {}) => {
      const userTenantService = app.service('api/auth/v1/tenant-users')
      const query = { ...defaultQuery, ...params.query }
      return await userTenantService.find({ ...params, query })
    },
    findSkyVisitor: async (params = {}) => {
      const skyVisitorService = app.service('api/auth/v1/skyvisitor')
      const query = params.query
      return await skyVisitorService.find({ ...params, query })
    },
    findLocationsByTenant: async (query: Query = {}): Promise<Location[]> => {
      const tenantId = await getTenantId()

      if (!tenantId) throw new Error('getLocationsClient: TenantId is required')

      return locationClient.service('/location/v1/:tenantId').find({ paginate: false, route: { tenantId }, query: query }) as any as Location[]
    },
    findCompaniesByTenant: async (query: Query = {}): Promise<Company[]> => {
      const tenantId = await getTenantId()

      if (!tenantId) throw new Error('findCompaniesByTenant: TenantId is required')
      return companiesClient.service('/company/v1/:tenantId').find({ paginate: false, route: { tenantId }, query: query }) as any as Company[]
    },
    findIssuableItemCategories: async (query: Query = {}): Promise<IssuableItemCategory[]> => {
      const tenantId = await getTenantId()

      if (!tenantId) throw new Error('getcompanyByTenant: TenantId is required')
      return usersClient.service('/user/v1/:tenantId/issuableItemCategory').find({ paginate: false, route: { tenantId }, query: query }) as any as IssuableItemCategory[]
    },

    getCompanyByTenant: async (companyId: string): Promise<Company | null> => {
      const tenantId = await getTenantId()

      if (!tenantId) throw new Error('getcompanyByTenant: TenantId is required')
      return companiesClient.service('/company/v1/:tenantId').get(companyId, { route: { tenantId } }) as any as Company | null
    },
    createCompanyByTenant: async (company: Company): Promise<Company | null> => {
      const tenantId = await getTenantId()

      if (!tenantId) throw new Error('getcompanyByTenant: TenantId is required')
      return companiesClient.service('/company/v1/:tenantId').create(company, { route: { tenantId } }) as any as Company | null
    },
    findCompaniesFuzzyByTenant: async (search: string): Promise<Company[]> => {
      const tenantId = await getTenantId()

      if (!tenantId) throw new Error('findCompaniesFuzzyByTenant: TenantId is required')
      const allCompanies = await companiesClient.service('/company/v1/:tenantId').find({ paginate: false, route: { tenantId } }) as any as Company[]
      const miniSearch = new MiniSearch({
        fields: ['name'], // fields to index for full-text search
        idField: 'companyId', // field to use as unique identifier
        searchOptions: {
          fuzzy: 0.2,
          prefix: true,
        },
      })
      miniSearch.addAll(allCompanies)
      return miniSearch.search(search).map(a => allCompanies.find(b => b.companyId == a.id)!)
    },
    findUsersByTenant: async (query: Query = {}): Promise<User[]> => {
      const tenantId = await getTenantId()

      if (!tenantId) throw new Error('findUsersByTenant: TenantId is required')

      return usersClient.service('/user/v1/:tenantId').find({ paginate: false, route: { tenantId }, query: query }) as any as User[]
    },
    patchUserByTenant: async (userId: string, user: User): Promise<User> => {
      const tenantId = await getTenantId()

      if (!tenantId) throw new Error('patchUserByTenant: TenantId is required')
      return usersClient.service('/user/v1/:tenantId').patch(userId, user, { route: { tenantId } }) as any as User
    },
    createUserByTenant: async (user: User): Promise<User> => {
      const tenantId = await getTenantId()

      if (!tenantId) throw new Error('createUserByTenant: TenantId is required')
      return usersClient.service('/user/v1/:tenantId').create(user, { route: { tenantId } }) as any as User
    },
    findUserByTenant: async (userId: string): Promise<User> => {
      const tenantId = await getTenantId()
      if (!tenantId) throw new Error('findUserByTenant: TenantId is required')
      return usersClient.service('/user/v1/:tenantId').get(userId, { route: { tenantId } })
    },

    findUsersNoteTypesByTenant: async (query: Query = {}): Promise<NoteTypes[]> => {
      const tenantId = await getTenantId()

      if (!tenantId) throw new Error('findUsersNoteTypesByTenant: TenantId is required')

      return usersClient.service('/user/v1/:tenantId/note-type').find({ paginate: false, route: { tenantId }, query: query }) as any as NoteTypes[]
    },
    findUsersTitlesByTenant: async (query: Query = {}): Promise<Title[]> => {
      const tenantId = await getTenantId()

      if (!tenantId) throw new Error('findUsersTitlesByTenant: TenantId is required')

      return usersClient.service('/user/v1/:tenantId/title').find({ paginate: false, route: { tenantId }, query: query }) as any as Title[]
    },

    findUserPhoneTypesByTenant: async (): Promise<PhoneType[]> => {
      const tenantId = await getTenantId()

      if (!tenantId) throw new Error('findUsersTitlesByTenant: TenantId is required')

      return usersClient.service('/user/v1/:tenantId/phone-type').find({ paginate: false, route: { tenantId } }) as any as PhoneType[]
    },

    findUsersUserFieldsByTenant: async (query: Query = {}): Promise<UserFields[]> => {
      const tenantId = await getTenantId()

      if (!tenantId) throw new Error('findUsersUserFieldsByTenant: TenantId is required')

      return usersClient.service('/user/v1/:tenantId/user-field').find({ paginate: false, route: { tenantId }, query: query }) as any as UserFields[]
    },

    findUsersPhotoByTenant: async (userIds: string[]): Promise<UserPhoto[]> => {
      const tenantId = await getTenantId()

      if (!tenantId) throw new Error('findUsersPhotoByTenant: TenantId is required')

      return usersClient.service('/user/v1/:tenantId/user-photo').find({ paginate: false, route: { tenantId }, query: { userId: { $in: userIds } } }) as any as UserPhoto[]
    },

    findUserPhotoByTenant: async (userId: string): Promise<UserPhoto | null> => {
      const tenantId = await getTenantId()

      if (!tenantId) throw new Error('findUserPhotoByTenant: TenantId is required')

      try {
        return await usersClient.service('/user/v1/:tenantId/user-photo').get(userId, { route: { tenantId } }) as any as UserPhoto
      }
      catch (e) {
        return null
      }
    },

    updateUserPhotoByTenant: async (userId: string, photoBase64: string): Promise<boolean> => {
      const tenantId = await getTenantId()

      if (!tenantId) throw new Error('findUserPhotoByTenant: TenantId is required')

      try {
        await usersClient.service('/user/v1/:tenantId/user-photo').update(userId, { userId: userId, photo: photoBase64 }, { route: { tenantId } }) as any as UserPhoto
      }
      catch (e) {
        return false
      }
      return true
    },

    bookingValidate: async (booking: ValidateData): Promise<ValidateResult[]> => {
      const tenantId = await getTenantId()

      if (!tenantId) throw new Error('bookingValidate: TenantId is required')

      return bookingClient.service('/booking/v1/:tenantId/validate').create(booking, { route: { tenantId } }) as any as ValidateResult[]
    },

    getVisitUserByTenant: async (visitUserId: string): Promise<VisitUserDetail> => {
      const tenantId = await getTenantId()

      if (!tenantId) throw new Error('bookingValidate: TenantId is required')

      return bookingClient.service('/booking/v1/:tenantId/visit-user').get(visitUserId, { route: { tenantId } }) as any as VisitUserDetail
    },

    bookingCreate: async (booking: BookingData): Promise<CreateBookingResult> => {
      const tenantId = await getTenantId()

      if (!tenantId) throw new Error('bookingCreate: TenantId is required')

      return bookingClient.service('/booking/v1/:tenantId').create(booking, { route: { tenantId } }) as any as CreateBookingResult
    },
    updateVisitSchedule: async (visitId: string, fromDate: string, toDate: string): Promise<UpdateVisitScheduleResult> => {
      const tenantId = getTenantId()
      if (!tenantId) throw new Error('updateVisitSchedule: TenantId is required')

      const response = await authedFetch(`/api/booking/v1/${tenantId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Service-Method': 'updateVisitSchedule',
        },
        body: JSON.stringify({ visitId, fromDate, toDate }),
      })
      const result = await response.json()
      if (!response.ok) throw new Error(result.message ?? 'Unable to update visit')
      return result as UpdateVisitScheduleResult
    },
    visitorCheckin: async (visitId: string, visitUserId: string, proceed: boolean = false, first: boolean = false): Promise<CheckinResult> => {
      const tenantId = await getTenantId()

      if (!tenantId) throw new Error('visitorCheckin: TenantId is required')
      return bookingClient.service('/booking/v1/:tenantId').checkin({
        visitId: visitId,
        visitUserId: visitUserId,
        first: first,
        proceed: proceed,
      } as any, { route: { tenantId } }) as any as CheckinResult
    },
    visitorCheckout: async (visitId: string, visitUserId: string, proceed: boolean = false, first: boolean = false): Promise<CheckoutResult> => {
      const tenantId = await getTenantId()

      if (!tenantId) throw new Error('visitorCheckOut: TenantId is required')
      return bookingClient.service('/booking/v1/:tenantId').checkout({
        visitId: visitId,
        visitUserId: visitUserId,
        first: first,
        proceed: proceed,
      } as any, { route: { tenantId } }) as any as CheckoutResult
    },

    patchVisitUser: async (data: { visitUserId: string } & Partial<VisitUserDetail>, selfEdit: boolean): Promise<boolean> => {
      const tenantId = await getTenantId()

      if (!tenantId) throw new Error('visitUserPatch: TenantId is required')
      await bookingClient.service('/booking/v1/:tenantId/visit-user').patch(data.visitUserId, data, { route: { tenantId }, headers: { 'self-edit': selfEdit } })
      return true
    },

    visitorsValidate: async (locations: number[], visitors: Visitor[], forCheckin: boolean | string = false): Promise<VisitorResult | null> => {
      const tenantId = await getTenantId()
      if (!tenantId) throw new Error('visitorsValidate: TenantId is required')
      const valResult = await bookingClient.service('/booking/v1/:tenantId/validate').validateVisitors({ visitLocations: locations, visitors: visitors, forCheckin: forCheckin }, { route: { tenantId } }) as any as VisitorResult[]
      if (valResult.length > 0) return valResult[0]
      return null
    },
    findVisitTypesByTenant: async (query: Query = {}): Promise<VisitType[]> => {
      const tenantId = await getTenantId()

      if (!tenantId) throw new Error('findVisitTypesByTenant: TenantId is required')
      console.log('findVisitTypesByTenant', query)
      return bookingClient.service('/booking/v1/:tenantId/visit-type').find({ paginate: false, route: { tenantId }, query: query }) as any as VisitType[]
    },
    findVisitsByTenant: async (query: Query = {}): Promise<Visit[]> => {
      const tenantId = await getTenantId()

      if (!tenantId) throw new Error('findVisitsByTenant: TenantId is required')

      return bookingClient.service('/booking/v1/:tenantId/visit').find({ paginate: false, route: { tenantId }, query: query }) as any as Visit[]
    },
    findVisitByTenant: async (visitId: string): Promise<Visit> => {
      const tenantId = await getTenantId()
      if (!tenantId) throw new Error('findVisitsByTenant: TenantId is required')

      return bookingClient.service('/booking/v1/:tenantId/visit').get(visitId, { route: { tenantId } }) as any as Visit
    },
    findVisitUsersTypesByTenant: async (query: Query = {}): Promise<VisitUserType[]> => {
      const tenantId = await getTenantId()

      if (!tenantId) throw new Error('findVisitUsersTypesByTenant: TenantId is required')

      return bookingClient.service('/booking/v1/:tenantId/visit-user-type').find({ paginate: false, route: { tenantId }, query: query }) as any as VisitUserType[]
    },
    findUserAnnounceProtocolsByTenant: async (): Promise<UserAnnounceProtocol[]> => {
      const tenantId = await getTenantId()

      if (!tenantId) throw new Error('findUserAnnounceProtocolsByTenant: TenantId is required')

      return usersClient.service('/user/v1/:tenantId/announce-protocol').find({ paginate: false, route: { tenantId } }) as any as UserAnnounceProtocol[]
    },
    findUserSpecialAssitanceNeedByTenant: async (): Promise<UserSpecialAssistanceNeed[]> => {
      const tenantId = await getTenantId()

      if (!tenantId) throw new Error('findUserSpecialAssitanceNeedByTenant: TenantId is required')

      return usersClient.service('/user/v1/:tenantId/special-assistance-need').find({ paginate: false, route: { tenantId } }) as any as UserSpecialAssistanceNeed[]
    },
    findCustomFieldTypesByTenant: async (): Promise<CustomFieldType[]> => {
      const tenantId = await getTenantId()

      if (!tenantId) throw new Error('findCustomFieldTypesByTenant: TenantId is required')

      return customFieldTypesClient.service('/custom-field-type/v1/:tenantId').find({ paginate: false, route: { tenantId } }) as any as CustomFieldType[]
    },
    findUserFieldsByTenant: async (): Promise<UserFields[]> => {
      const tenantId = await getTenantId()

      if (!tenantId) throw new Error('findUSerFieldsByTenant: TenantId is required')

      return usersClient.service('/user/v1/:tenantId/user-field').find({ paginate: false, route: { tenantId } }) as any as UserFields[]
    },
    findVisitUserFieldsByTenant: async (): Promise<VisitUserField[]> => {
      const tenantId = await getTenantId()

      if (!tenantId) throw new Error('findVisitUSerFieldsByTenant: TenantId is required')

      return bookingClient.service('/booking/v1/:tenantId/visit-user-field').find({ paginate: false, route: { tenantId } }) as any as VisitUserField[]
    },

    findVisitUserRequiredEntryFieldsByTenant: async (): Promise<VisitUserRequiredEntryField[]> => {
      const tenantId = await getTenantId()

      if (!tenantId) throw new Error('findVisitUserRequiredEntryFieldsByTenant: TenantId is required')

      return bookingClient.service('/booking/v1/:tenantId/visit-user-required-entry-field').find({ paginate: false, route: { tenantId } }) as any as VisitUserRequiredEntryField[]
    },
    findNationalities: async (): Promise<Nationality[]> => {
      const tenantId = await getTenantId()

      if (!tenantId) throw new Error('findNationalities: TenantId is required')

      return usersClient.service('/user/v1/:tenantId/nationality').find({ paginate: false, route: { tenantId } }) as any as Nationality[]
    },
    createPasswordResetToken: async (login: string, database: string): Promise<number> => {
      const userTenantService = app.service('api/auth/v1/tenant-users')
      // NOTE: This does not return a token, which doesn't matter as it will go out via email anyway
      return await userTenantService.create({ login, database }, {
        headers: {
          'X-Service-Method': 'createPasswordResetToken',
        },
      })
    },
    validatePasswordResetToken: async (login: string, database: string, token: string): Promise<number> => {
      const userTenantService = app.service('api/auth/v1/tenant-users')
      return await userTenantService.create({ login, database, token }, {
        headers: {
          'X-Service-Method': 'validatePasswordResetToken',
        },
      })
    },
    resetPassword: async (login: string, database: string, token: string, password: string): Promise<number> => {
      const userTenantService = app.service('api/auth/v1/tenant-users')
      return await userTenantService.create({ login, database, token, password }, {
        headers: {
          'X-Service-Method': 'resetPassword',
        },
      })
    },

    toggleSimpleBooking: async (): Promise<boolean> => {
      const tenantId = await getTenantId()
      const user = await dataService.getCurrentUser()
      await usersClient.service('/user/v1/:tenantId').toggleSimpleBooking({ userId: user.user.id }, { route: { tenantId } })
      return true
    },

    fetchAndUpdateNewEvents: async (deviceSerial: number): Promise<MocaEventsResult[]> => {
      const tenantId = await getTenantId()
      return integrationClient.service('/integration/v1/moca-web-hook').fetchAndUpdateNewEvents({
        deviceSerial: deviceSerial,
        database: tenantId!,
      })
    },
    reserveCardForVisitor: async (cardData: ReserveDetails): Promise<boolean> => {
      const tenantId = await getTenantId()
      cardData.forceReserve = cardData.forceReserve ?? true
      return (await usersClient.service('/user/v1/:tenantId/issuable').reserveCardForVisitor(cardData, { route: { tenantId } })).success
    },
    executeReport: async (reportName: string, payload: any) => {
      const tenantId = await getTenantId()
      return (await reportClient.service('/report/v1/:tenantId').executeReport({ reportId: reportName, payload }, {
        route: { tenantId },
        paginate: false,
      }))
    },

    getPass: async (data: { visitId: string, visitUserId: string }[]) => {
      const tenantId = await getTenantId()
      return (await reportClient.service('/report/v1/:tenantId/badge').generateBadge(data, {
        route: { tenantId },
      }))
    },
  }

  return {
    provide: {
      dataService,
    },
  }
})
